import { useState } from 'react';
import axios from 'axios';
import { X, Repeat } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventForm = ({ onClose, onEventCreated, editingEvent }) => {
  const [formData, setFormData] = useState({
    title: editingEvent?.title || '',
    date: editingEvent?.date || '',
    time: editingEvent?.time || '',
    location: editingEvent?.location || '',
  });
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringOptions, setRecurringOptions] = useState({
    frequency: 'weekly',
    count: 4,
  });
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editingEvent;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    let success = false;
    let possibleSuccess = false; // Event might have been created even if we got error
    
    try {
      if (isEditing) {
        await axios.put(`${API}/events/${editingEvent.id}`, formData, { timeout: 10000 });
        toast.success('Event updated!');
        success = true;
      } else if (isRecurring) {
        const events = [];
        const startDate = new Date(formData.date);
        
        for (let i = 0; i < recurringOptions.count; i++) {
          const eventDate = new Date(startDate);
          
          if (recurringOptions.frequency === 'weekly') {
            eventDate.setDate(startDate.getDate() + (i * 7));
          } else if (recurringOptions.frequency === 'biweekly') {
            eventDate.setDate(startDate.getDate() + (i * 14));
          } else if (recurringOptions.frequency === 'monthly') {
            eventDate.setMonth(startDate.getMonth() + i);
          }
          
          const eventData = {
            ...formData,
            date: eventDate.toISOString().split('T')[0],
          };
          
          events.push(axios.post(`${API}/events`, eventData, { timeout: 10000 }));
        }
        
        await Promise.all(events);
        toast.success(`${recurringOptions.count} events created!`);
        success = true;
      } else {
        await axios.post(`${API}/events`, formData, { timeout: 10000 });
        toast.success('Event created!');
        success = true;
      }
    } catch (error) {
      console.error('Error creating event:', error);
      
      // Check if it's a timeout or network error - event might still be created
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.warning('Request timed out - checking if event was created...');
        possibleSuccess = true;
      } else if (!error.response) {
        // Network error - request might have succeeded
        toast.warning('Network issue - event may have been created. Refreshing...');
        possibleSuccess = true;
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} event`);
      }
    } finally {
      setSubmitting(false);
      
      // Refresh events list if successful OR if there's a chance it succeeded
      if (success || possibleSuccess) {
        try {
          await onEventCreated(); // This refreshes the events list
          onClose();
        } catch (err) {
          console.error('Error refreshing:', err);
        }
      }
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Anton'] text-2xl text-white uppercase tracking-wide">
          {isEditing ? 'Edit Event' : 'Create Event'}
        </h3>
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
            Event Title
          </label>
          <input
            data-testid="event-title-input"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="Long Island Iced Tea Party"
            className="w-full bg-zinc-800 border border-white/10 text-white p-3 focus:border-[#E11D48] focus:outline-none transition-colors"
          />
        </div>

        <div>
          <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
            Date
          </label>
          <input
            data-testid="event-date-input"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            className="w-full bg-zinc-800 border border-white/10 text-white p-3 focus:border-[#E11D48] focus:outline-none transition-colors"
          />
        </div>

        <div>
          <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
            Time
          </label>
          <input
            data-testid="event-time-input"
            type="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            required
            className="w-full bg-zinc-800 border border-white/10 text-white p-3 focus:border-[#E11D48] focus:outline-none transition-colors"
          />
        </div>

        <div>
          <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
            Location
          </label>
          <input
            data-testid="event-location-input"
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            placeholder="The Kings Arms, Bexleyheath"
            className="w-full bg-zinc-800 border border-white/10 text-white p-3 focus:border-[#E11D48] focus:outline-none transition-colors"
          />
        </div>

        {/* Recurring Events Toggle - Only for new events */}
        {!isEditing && (
          <div className="border-t border-white/10 pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="w-5 h-5 bg-zinc-800 border border-white/10 text-[#E11D48] focus:ring-[#E11D48] focus:ring-2"
              />
              <div className="flex items-center gap-2 text-zinc-300">
                <Repeat className="w-4 h-4" />
                <span className="uppercase tracking-wider text-sm font-bold">Create Recurring Events</span>
              </div>
            </label>

            {isRecurring && (
              <div className="mt-4 space-y-3 pl-8 animate-in fade-in duration-300">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-2">
                      Frequency
                    </label>
                    <select
                      value={recurringOptions.frequency}
                      onChange={(e) => setRecurringOptions({...recurringOptions, frequency: e.target.value})}
                      className="w-full bg-zinc-800 border border-white/10 text-white text-sm p-2 focus:border-[#E11D48] focus:outline-none"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-zinc-500 text-xs uppercase tracking-wider mb-2">
                      # of Events
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="12"
                      value={recurringOptions.count}
                      onChange={(e) => setRecurringOptions({...recurringOptions, count: parseInt(e.target.value)})}
                      className="w-full bg-zinc-800 border border-white/10 text-white text-sm p-2 focus:border-[#E11D48] focus:outline-none"
                    />
                  </div>
                </div>
                <p className="text-zinc-500 text-xs">
                  Will create {recurringOptions.count} events starting from selected date
                </p>
              </div>
            )}
          </div>
        )}

        <button
          data-testid="submit-event-button"
          type="submit"
          disabled={submitting}
          className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white font-['Anton'] text-xl uppercase tracking-widest py-3 transition-colors disabled:opacity-50 border-2 border-transparent hover:border-white/20"
        >
          {submitting ? 'Saving...' : isEditing ? 'Update Event' : isRecurring ? `Create ${recurringOptions.count} Events` : 'Create Event'}
        </button>
      </form>
    </div>
  );
};

export default EventForm;